// Re-export the components from react-menu in case we want to modify them later
// without changing downstream imports (proxy pattern).
/* eslint react-refresh/only-export-components: 0 */
import "./Menu.scss";

import {
  type ClickEvent,
  ControlledMenuProps,
  type EventHandler,
  type FocusableItemProps,
  type MenuChangeEvent,
  MenuItemProps,
  MenuProps,
  ControlledMenu as ReactControlledMenu,
  FocusableItem as ReactFocusableItem,
  Menu as ReactMenu,
  MenuItem as ReactMenuItem,
  SubMenu as ReactSubMenu,
  SubMenuProps,
} from "@szhsin/react-menu";
import clsx from "clsx";
import { type FC, type MouseEvent, type ReactNode, useState } from "react";

import { MenuOpenProvider } from "mds/components/MenuOpenContext";
import { ChevronRightIcon } from "mds/icons";
import { type AmplitudeEventPropertiesType, trackEvent } from "utils/amplitude";
import { Link, type LinkProps } from "utils/routing";

export {
  FocusableItem,
  MenuDivider,
  MenuGroup,
  MenuRadioGroup,
  MenuHeader,
  useClick,
  type MenuProps,
  type MenuDirection,
  type ClickEvent,
  type MenuInstance,
  type MenuChangeEvent,
} from "@szhsin/react-menu";

type MdsMenuProps = MenuProps & {
  limitItemHeight?: boolean;
};

/**
 * A simple wrapper around the `@szhsin/react-menu` `Menu` component that sets some default props
 * and css classes for MDS styling.
 * Follow the documentation in https://szhsin.github.io/react-menu/ for usage.
 * Components like MenuItem, MenuLinkItem, MenuDivider, and SubMenu are provided by this module.
 * They will be styled by this parent component.
 *
 * `menuButton` receives `data-selected` while open via context. Pass `isSelected` to override.
 */
export const Menu: FC<MdsMenuProps> = ({
  className,
  limitItemHeight = true,
  menuButton,
  onMenuChange,
  ...menuProps
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuChange = (event: MenuChangeEvent) => {
    setIsMenuOpen(event.open);
    onMenuChange?.(event);
  };

  return (
    <MenuOpenProvider value={isMenuOpen}>
      <ReactMenu
        align="center"
        className={clsx(
          "mds-menu",
          !menuProps.children && "empty",
          limitItemHeight && "limit-item-height",
          className,
        )}
        direction="bottom"
        overflow="auto"
        position="auto"
        viewScroll="auto"
        portal
        {...menuProps}
        menuButton={menuButton}
        onMenuChange={handleMenuChange}
      >
        {menuProps.children}
      </ReactMenu>
    </MenuOpenProvider>
  );
};

type SubMenuArrowProps = SubMenuProps & { withArrow?: boolean; iconColor?: string };

/**
 * A simple wrapper around the SubMenu allowing for some additional styling
 */
export const SubMenu: FC<SubMenuArrowProps & { allowPropagation?: boolean }> = ({
  label,
  withArrow,
  children,
  className,
  onClick,
  allowPropagation = false,
  ...subMenuProps
}) => {
  const labelWithArrow = withArrow ? (
    <>
      {label}
      <span className="menu__withArrow">
        <ChevronRightIcon size="s" />
      </span>
    </>
  ) : (
    label
  );

  const onClickHandler = (e: MouseEvent<HTMLElement>) => {
    if (!allowPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <ReactSubMenu
      className={clsx("interactive", className)}
      direction="right"
      label={labelWithArrow}
      overflow="auto"
      onClick={onClickHandler}
      {...subMenuProps}
    >
      {children}
    </ReactSubMenu>
  );
};

export type MenuItemKind =
  | "destructive"
  | "instructional"
  | "radio"
  | "page-dropdown"
  | "topic-dropdown";

type MenuItemKindProps = MenuItemProps & {
  eventName?: string;
  eventProps?: Omit<AmplitudeEventPropertiesType, "eventCategory">;
  kind?: MenuItemKind;
  iconColor?: string;
  download?: string;
  allowPropagation?: boolean;
};

/**
 * A simple wrapper around the SubMenu allowing for some additional styling
 */
export const MenuItem: FC<MenuItemKindProps> = ({
  kind,
  className,
  eventName,
  eventProps,
  onClick,
  allowPropagation = false,
  ...menuItemProps
}) => {
  const clickHandler: EventHandler<ClickEvent> | undefined =
    eventName || onClick
      ? (e) => {
          if (eventName) {
            trackEvent(eventName, { eventCategory: "Button press", ...eventProps });
          }
          if (!allowPropagation) {
            e.syntheticEvent.stopPropagation();
            e.syntheticEvent.preventDefault();
          }

          if (onClick) {
            onClick(e);
          }
        }
      : undefined;

  return (
    <ReactMenuItem
      className={clsx(
        "mds-menu interactive",
        kind && `szh-menu__item-${kind}`,
        kind === "radio" &&
          "before:size-4 before:rounded-full before:border before:border-black-tint-40 before:bg-black-tint-99 " +
            "aria-checked:before:size-2 aria-checked:before:border-[5px] aria-checked:before:border-blue",
        className,
      )}
      onClick={clickHandler}
      {...menuItemProps}
    >
      {menuItemProps.children}
    </ReactMenuItem>
  );
};

type MenuLinkItemProps = Omit<FocusableItemProps, "children"> & {
  to: LinkProps["to"];
  target?: LinkProps["target"];
  selected?: boolean;
  eventName?: string;
  eventProps?: Omit<AmplitudeEventPropertiesType, "eventCategory">;
  children: ReactNode;
};

/**
 * A menu item that navigates via a React Router Link.
 * Uses FocusableItem so the link is keyboard-operable and remains a semantic <a>.
 */
export const MenuLinkItem: FC<MenuLinkItemProps> = ({
  className,
  disabled,
  selected,
  eventName,
  eventProps,
  to,
  target,
  children,
  ...focusableItemProps
}) => {
  return (
    <ReactFocusableItem
      className={clsx("mds-menu interactive", selected && "selected")}
      disabled={disabled}
      {...focusableItemProps}
    >
      {({ ref, closeMenu }) => (
        <Link
          className={clsx("unlink flex w-full items-center gap-2", className)}
          ref={ref}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          target={target}
          to={to}
          onClick={(e) => {
            if (eventName) {
              trackEvent(eventName, { eventCategory: "Button press", ...eventProps });
            }
            closeMenu(e.detail === 0 ? "Enter" : undefined);
          }}
        >
          {children}
        </Link>
      )}
    </ReactFocusableItem>
  );
};

/**
 * See Menu. This is the equivalent for ControlledMenu.
 */
export const ControlledMenu: FC<ControlledMenuProps> = ({ className, ...menuProps }) => {
  return (
    <ReactControlledMenu
      align="center"
      className={clsx("mds-menu", className)}
      direction="bottom"
      overflow="auto"
      position="auto"
      viewScroll="auto"
      {...menuProps}
    >
      {menuProps.children}
    </ReactControlledMenu>
  );
};

export type MenuType = "menu" | "submenu" | "controlledmenu";
